// i18n.js — EN/IT locale string table for the Pronto redesign.
//
// The Language toggle (Settings) swaps the greeting + the 5 tab labels only.
// Standalone Italian "easter-egg" strings (Buongiorno, Suggerisci, conferma, the
// proverb …) are intentionally locale-INDEPENDENT — they read the same in both
// languages per docs/pronto-redesign.md §7. Grow STRINGS as screens migrate.
//
// Pure data + lookups; no React, no store coupling. Read `preferences.language`
// (Phase 3 column) and pass it in.

export const LOCALES = ["en", "it"];
export const DEFAULT_LOCALE = "en";

export function normalizeLocale(l) {
  return LOCALES.includes(l) ? l : DEFAULT_LOCALE;
}

// The 5 bottom-tab / sidebar labels. Ordered to match the tab bar.
export const TAB_LABELS = {
  today: { en: "Today", it: "Oggi" },
  coffee: { en: "Coffee", it: "Caffè" },
  cook: { en: "Cook", it: "Cucina" },
  kitchen: { en: "Kitchen", it: "Dispensa" },
  listen: { en: "Listen", it: "Ascolta" },
  ask: { en: "Ask", it: "Chiedi" },
};

export function tabLabel(id, locale = DEFAULT_LOCALE) {
  const entry = TAB_LABELS[id];
  return entry ? entry[normalizeLocale(locale)] : id;
}

// Translatable UI strings. Add keys here rather than inlining bilingual literals.
const STRINGS = {
  greeting_morning: { en: "Good morning", it: "Buongiorno" },
  greeting_afternoon: { en: "Good afternoon", it: "Buon pomeriggio" },
  greeting_evening: { en: "Good evening", it: "Buonasera" },
  view_mode: { en: "App mode", it: "Modalità" },
  language: { en: "Language", it: "Lingua" },
  settings: { en: "Settings", it: "Impostazioni" },
  sign_out: { en: "Sign out", it: "Esci" },
};

export function t(key, locale = DEFAULT_LOCALE) {
  const entry = STRINGS[key];
  if (!entry) return key;
  return entry[normalizeLocale(locale)] ?? entry[DEFAULT_LOCALE];
}

// Time-of-day greeting in the active locale. `hour` is 0–23 (local time).
export function greeting(hour, locale = DEFAULT_LOCALE) {
  const key = hour < 12 ? "greeting_morning" : hour < 18 ? "greeting_afternoon" : "greeting_evening";
  return t(key, locale);
}

// Locale-INDEPENDENT Italian flourishes. Same in EN and IT — reference these
// constants instead of hardcoding the strings so they stay consistent app-wide.
export const IT = {
  buongiorno: "Buongiorno",
  cosaCuciniamo: "Cosa cuciniamo?",
  andiamo: "Andiamo",
  suggerisci: "Suggerisci",
  conferma: "conferma",
  siAdd: "Sì, add",
  no: "No",
  usaSubito: "Usa subito",
  spesa: "Spesa",
  dispensa: "Dispensa",
  oggi: "Oggi",
  perfetto: "Perfetto ☕",
  buonAppetito: "Buon appetito!",
  pronto: "Pronto!",
  proverb: "A tavola non si invecchia.",
  proverbGloss: "at the table, one never grows old",
};

// The four Today view modes (matches the preferences.view_mode column).
export const VIEW_MODES = [
  { id: "adaptive", label: { en: "Adaptive", it: "Adattiva" } },
  { id: "ai", label: { en: "AI-first", it: "AI" } },
  { id: "hybrid", label: { en: "Hybrid", it: "Ibrida" } },
  { id: "traditional", label: { en: "Traditional", it: "Classica" } },
];

export function normalizeViewMode(v) {
  return VIEW_MODES.some((m) => m.id === v) ? v : "adaptive";
}
